import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { EpcEstimateWbsTreeSelect } from '@/components/epc/estimation/EpcEstimateWbsTreeSelect';
import { EST3_EQUIPMENT_COMPONENT_KINDS, EST3_EQUIPMENT_DURATION_UOMS } from '@/components/epc/estimation/est3EquipmentKinds';
import { EST3_LABOUR_RATE_BASES } from '@/components/epc/estimation/est3LabourKinds';
import { EST3_MATERIAL_COMPONENT_KINDS } from '@/components/epc/estimation/est3MaterialKinds';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { formatMoney } from '@/utils/format';
import {
  componentTotal,
  enrichComponentsForKind,
  parseTemplateComponents,
} from '@/utils/wbsLibraryTemplateComponents';

const { Text, Title } = Typography;

const RESOURCE_KINDS = ['MATERIAL', 'LABOUR', 'EQUIPMENT', 'SUBCONTRACT', 'OTHER'] as const;

export interface EpcWbsLibraryCostTemplateDrawerProps {
  open: boolean;
  onClose: () => void;
  editing?: Record<string, unknown> | null;
  cbsNodes: Array<{ id: string; code: string; name?: string; parent_id?: string | null }>;
  labourClasses: Array<{ id: string; code: string; name: string }>;
  equipmentClasses: Array<{ id: string; code: string; name: string }>;
  nodeLabel?: string;
  pending?: boolean;
  onSubmit: (payload: Record<string, unknown>) => void;
}

export const EpcWbsLibraryCostTemplateDrawer: React.FC<EpcWbsLibraryCostTemplateDrawerProps> = ({
  open,
  onClose,
  editing,
  cbsNodes,
  labourClasses,
  equipmentClasses,
  nodeLabel,
  pending,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const resourceKind = Form.useWatch('resourceKind', form) || 'MATERIAL';
  const defaultQuantity = Form.useWatch('defaultQuantity', form) ?? 1;
  const defaultRate = Form.useWatch('defaultRate', form) ?? 0;
  const componentsWatch = Form.useWatch('components', form) || [];
  const [liveComponentTotal, setLiveComponentTotal] = useState(0);

  const classOptions = useMemo(() => {
    const list = resourceKind === 'LABOUR' ? labourClasses : equipmentClasses;
    return list.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}`, code: c.code, name: c.name }));
  }, [resourceKind, labourClasses, equipmentClasses]);

  const classById = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    for (const o of classOptions) m.set(o.value, { code: o.code, name: o.name });
    return m;
  }, [classOptions]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const components = parseTemplateComponents(editing.meta_json);
      form.setFieldsValue({
        cbsCodeId: editing.cbs_code_id,
        resourceKind: editing.resource_kind,
        description: editing.description,
        defaultQuantity: editing.default_quantity,
        defaultRate: editing.default_rate,
        uom: editing.uom,
        resourceClassId: editing.source_ref_id || undefined,
        components: components.length
          ? components.map((c) => ({
              resourceClassId: c.resourceClassId,
              itemId: c.itemId || c.resourceClassId,
              itemCode: c.itemCode,
              itemName: c.itemName,
              description: c.description,
              uom: c.uom,
              rateBasis: c.rateBasis,
              headcount: c.headcount,
              hours: c.hours,
              ownershipMode: c.ownershipMode,
              componentKind: c.componentKind,
              quantity: c.quantity,
              duration: c.duration,
              durationUom: c.durationUom,
              rate: c.rate,
            }))
          : editing.source_ref_id
            ? editing.resource_kind === 'MATERIAL'
              ? [{ itemId: editing.source_ref_id, rate: editing.default_rate, quantity: 1, componentKind: 'BASE' }]
              : [{ resourceClassId: editing.source_ref_id, rate: editing.default_rate }]
            : [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        resourceKind: 'MATERIAL',
        defaultQuantity: 1,
        defaultRate: 0,
        components: [],
      });
    }
  }, [open, editing, form]);

  useEffect(() => {
    if (resourceKind !== 'LABOUR' && resourceKind !== 'EQUIPMENT' && resourceKind !== 'MATERIAL') {
      setLiveComponentTotal(0);
      return;
    }
    const enriched = enrichComponentsForKind(resourceKind, componentsWatch, classById);
    setLiveComponentTotal(componentTotal(enriched));
  }, [componentsWatch, resourceKind, classById]);

  const hasBuildUp =
    resourceKind === 'LABOUR' || resourceKind === 'EQUIPMENT' || resourceKind === 'MATERIAL';

  const displayTotal =
    hasBuildUp && componentsWatch.length
      ? liveComponentTotal
      : (Number(defaultQuantity) || 0) * (Number(defaultRate) || 0);

  const handleFinish = (v: Record<string, unknown>) => {
    const kind = String(v.resourceKind || 'MATERIAL');
    const rawComponents = Array.isArray(v.components) ? v.components : [];
    let payload: Record<string, unknown> = {
      cbsCodeId: v.cbsCodeId,
      resourceKind: kind,
      description: v.description,
      defaultQuantity: v.defaultQuantity,
      uom: v.uom,
    };

    if (kind === 'LABOUR' || kind === 'EQUIPMENT' || kind === 'MATERIAL') {
      const components = enrichComponentsForKind(kind, rawComponents as any[], classById);
      if (components.length) {
        payload = {
          ...payload,
          components,
          defaultRate: componentTotal(components) / (Number(v.defaultQuantity) || 1),
        };
      } else if (v.resourceClassId && kind !== 'MATERIAL') {
        const cls = classById.get(String(v.resourceClassId));
        payload = {
          ...payload,
          defaultRate: v.defaultRate,
          sourceRefType: kind === 'LABOUR' ? 'LABOUR_CLASS' : 'EQUIPMENT_CLASS',
          sourceRefId: v.resourceClassId,
          sourceRefLabel: cls?.code,
          clearComponents: true,
        };
      } else {
        payload = { ...payload, defaultRate: v.defaultRate, clearComponents: true };
      }
    } else {
      payload = { ...payload, defaultRate: v.defaultRate, clearComponents: true };
    }
    onSubmit(payload);
  };

  const materialColumns = [
    {
      title: 'Item',
      width: 200,
      render: (_: unknown, __: unknown, index: number) => (
        <Form.Item name={['components', index, 'itemId']} style={{ marginBottom: 0 }}>
          <ItemSelect allowClear placeholder="Inventory item" />
        </Form.Item>
      ),
    },
    {
      title: 'Kind',
      width: 100,
      render: (_: unknown, __: unknown, index: number) => (
        <Form.Item name={['components', index, 'componentKind']} initialValue="BASE" style={{ marginBottom: 0 }}>
          <Select options={EST3_MATERIAL_COMPONENT_KINDS.map((v) => ({ value: v, label: v }))} />
        </Form.Item>
      ),
    },
    {
      title: 'Qty',
      width: 72,
      render: (_: unknown, __: unknown, index: number) => (
        <Form.Item name={['components', index, 'quantity']} initialValue={1} style={{ marginBottom: 0 }}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      ),
    },
    {
      title: 'UoM',
      width: 72,
      render: (_: unknown, __: unknown, index: number) => (
        <Form.Item name={['components', index, 'uom']} style={{ marginBottom: 0 }}>
          <Input placeholder="ea" />
        </Form.Item>
      ),
    },
    {
      title: 'Rate',
      width: 96,
      render: (_: unknown, __: unknown, index: number) => (
        <Form.Item name={['components', index, 'rate']} style={{ marginBottom: 0 }}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      ),
    },
  ];

  const componentColumns =
    resourceKind === 'MATERIAL'
      ? materialColumns
      : resourceKind === 'LABOUR'
      ? [
          {
            title: 'Class',
            width: 180,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item
                name={['components', index, 'resourceClassId']}
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: 0 }}
              >
                <Select showSearch optionFilterProp="label" options={classOptions} placeholder="Labour class" />
              </Form.Item>
            ),
          },
          {
            title: 'Basis',
            width: 100,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'rateBasis']} initialValue="NORMAL" style={{ marginBottom: 0 }}>
                <Select options={EST3_LABOUR_RATE_BASES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            ),
          },
          {
            title: 'HC',
            width: 72,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'headcount']} initialValue={1} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
          {
            title: 'Hours',
            width: 80,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'hours']} initialValue={8} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
          {
            title: 'Rate',
            width: 96,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'rate']} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
        ]
      : [
          {
            title: 'Class',
            width: 180,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item
                name={['components', index, 'resourceClassId']}
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: 0 }}
              >
                <Select showSearch optionFilterProp="label" options={classOptions} placeholder="Equipment class" />
              </Form.Item>
            ),
          },
          {
            title: 'Kind',
            width: 96,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'componentKind']} initialValue="BASE" style={{ marginBottom: 0 }}>
                <Select options={EST3_EQUIPMENT_COMPONENT_KINDS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            ),
          },
          {
            title: 'Qty',
            width: 64,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'quantity']} initialValue={1} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
          {
            title: 'Dur',
            width: 72,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'duration']} initialValue={1} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
          {
            title: 'UoM',
            width: 88,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'durationUom']} initialValue="DAY" style={{ marginBottom: 0 }}>
                <Select options={EST3_EQUIPMENT_DURATION_UOMS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            ),
          },
          {
            title: 'Rate',
            width: 96,
            render: (_: unknown, __: unknown, index: number) => (
              <Form.Item name={['components', index, 'rate']} style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
        ];

  return (
    <Drawer
      title={editing ? 'Edit cost template' : 'Add cost template'}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      extra={
        <Button type="primary" loading={pending} onClick={() => form.submit()}>
          {editing ? 'Save changes' : 'Add template'}
        </Button>
      }
    >
      {nodeLabel ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={2}>
            <Text type="secondary">Package node</Text>
            <Text strong>{nodeLabel}</Text>
          </Space>
        </Card>
      ) : null}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Title level={5} style={{ marginTop: 0 }}>
          Classification
        </Title>
        <Form.Item name="cbsCodeId" label="CBS code" rules={[{ required: true, message: 'Select CBS' }]}>
          <EpcEstimateWbsTreeSelect kind="cbs" nodes={cbsNodes} />
        </Form.Item>
        <Form.Item name="resourceKind" label="Resource kind" rules={[{ required: true }]}>
          <Select
            options={RESOURCE_KINDS.map((k) => ({ value: k, label: k }))}
            onChange={() => form.setFieldsValue({ components: [], resourceClassId: undefined })}
          />
        </Form.Item>
        <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Enter description' }]}>
          <Input placeholder="Template description for estimators" />
        </Form.Item>

        {hasBuildUp ? (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>
                Resource build-up
              </Title>
              <Tag color="blue">{componentsWatch.length} line(s)</Tag>
            </div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {resourceKind === 'MATERIAL'
                ? 'Add one or more material / landed-cost lines. Totals roll into the template rate on plug.'
                : `Add one or more ${resourceKind === 'LABOUR' ? 'labour classes' : 'equipment classes'}. Totals roll into the template rate on plug.`}
            </Text>
            <Form.List name="components">
              {(fields, { add, remove }) => (
                <>
                  <Table
                    size="small"
                    pagination={false}
                    scroll={{ x: 640 }}
                    dataSource={fields}
                    rowKey="key"
                    columns={[
                      ...componentColumns,
                      {
                        title: '',
                        width: 40,
                        fixed: 'right' as const,
                        render: (_: unknown, __: unknown, index: number) => (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(index)}
                          />
                        ),
                      },
                    ]}
                  />
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                    onClick={() =>
                      add(
                        resourceKind === 'LABOUR'
                          ? { rateBasis: 'NORMAL', headcount: 1, hours: 8, rate: 0 }
                          : resourceKind === 'MATERIAL'
                            ? { componentKind: 'BASE', quantity: 1, rate: 0 }
                            : { componentKind: 'BASE', quantity: 1, duration: 1, durationUom: 'DAY', rate: 0 }
                      )
                    }
                  >
                    Add{' '}
                    {resourceKind === 'LABOUR'
                      ? 'labour'
                      : resourceKind === 'MATERIAL'
                        ? 'material'
                        : 'equipment'}{' '}
                    resource
                  </Button>
                </>
              )}
            </Form.List>
          </>
        ) : null}

        <Divider style={{ margin: '16px 0' }} />
        <Title level={5}>Economics</Title>
        <Space style={{ width: '100%' }} size="middle">
          <Form.Item name="defaultQuantity" label="Default quantity" style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="uom" label="Unit of measure" style={{ flex: 1 }}>
            <Input placeholder={resourceKind === 'LABOUR' ? 'HOUR' : resourceKind === 'EQUIPMENT' ? 'DAY' : 'ea'} />
          </Form.Item>
        </Space>
        {!hasBuildUp || !componentsWatch.length ? (
          <Form.Item name="defaultRate" label="Default rate">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ) : (
          <Form.Item label="Derived rate (from build-up)">
            <Input
              readOnly
              value={formatMoney(
                liveComponentTotal / (Number(defaultQuantity) || 1),
                { display: 'plain', maximumFractionDigits: 2 }
              )}
            />
          </Form.Item>
        )}

        <Card size="small" type="inner" style={{ marginTop: 8 }}>
          <Text type="secondary">Template total</Text>
          <div>
            <Text strong style={{ fontSize: 20 }}>
              {formatMoney(displayTotal, { display: 'plain', maximumFractionDigits: 2 })}
            </Text>
            {hasBuildUp && componentsWatch.length > 0 ? (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({componentsWatch.length} line{componentsWatch.length === 1 ? '' : 's'})
              </Text>
            ) : null}
          </div>
        </Card>
      </Form>
    </Drawer>
  );
};
